import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PhoneIcon, MenuIcon, XIcon, MessageSquareIcon } from 'lucide-react';
const NAV_LINKS = [{
  name: 'Home',
  href: '#home'
}, {
  name: 'Services',
  href: '#services'
}, {
  name: 'Before & After',
  href: '#before-after'
}, {
  name: 'Service Areas',
  href: '#service-areas'
}, {
  name: 'Contact',
  href: '#contact'
}];
interface NavigationProps {
  promoVisible: boolean;
}
// Promo bar height in px
const PROMO_HEIGHT = 40;
export function Navigation({
  promoVisible
}: NavigationProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navTop = promoVisible ? PROMO_HEIGHT : 0;
  return <header className="fixed left-0 right-0 transition-all duration-300" style={{
    top: navTop,
    zIndex: 50,
    background: isScrolled ? 'rgba(10,22,40,0.97)' : 'rgba(10,22,40,0.80)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    boxShadow: isScrolled ? '0 4px 30px rgba(0,0,0,0.4)' : 'none',
    borderBottom: isScrolled ? '1px solid rgba(11,122,196,0.2)' : '1px solid transparent',
    paddingTop: isScrolled ? '12px' : '18px',
    paddingBottom: isScrolled ? '12px' : '18px'
  }} data-id="element-214">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-215">
        <div className="flex justify-between items-center" data-id="element-216">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-3 group" data-id="element-217">
            <img src="https://cdn.magicpatterns.com/uploads/rXf2f64rQkMtLZPBVphvGA/ChatGPT_Image_Mar_16,_2026,_10_01_47_AM.png" alt="NJ Four Seasons Logo" className="w-14 h-14 object-contain flex-shrink-0 drop-shadow-[0_2px_10px_rgba(11,122,196,0.5)]" data-id="element-218" />
            <div className="flex flex-col leading-tight" data-id="element-219">
              <span className="text-white font-black text-lg tracking-tight group-hover:text-[#0B7AC4] transition-colors duration-300" data-id="element-220">
                NJ Four Seasons
              </span>
              <span className="text-gray-400 text-[10px] uppercase tracking-[0.15em] font-semibold" data-id="element-221">
                Gutter Cleaning
              </span>
            </div>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-7" data-id="element-222">
            {NAV_LINKS.map(link => <a key={link.name} href={link.href} className="relative text-gray-300 hover:text-white text-sm font-semibold transition-colors duration-200 py-1 group" data-id="element-223">
                {link.name}
                <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-[#0B7AC4] rounded-full transition-all duration-300 group-hover:w-full" data-id="element-224" />
              </a>)}
          </nav>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3" data-id="element-225">
            <a href="#contact" className="text-sm font-bold text-[#0B7AC4] hover:text-white transition-colors px-4 py-2 rounded-lg border border-[#0B7AC4]/40 hover:border-[#0B7AC4] hover:bg-[#0B7AC4]/10 uppercase tracking-wide" data-id="element-226">
              Free Estimate
            </a>
            <a href="tel:8624072386" className="relative inline-flex items-center gap-2 bg-[#FFD400] text-[#0A1628] px-5 py-2.5 rounded-lg font-bold text-sm overflow-hidden transition-all duration-300 hover:-translate-y-0.5 uppercase tracking-wide" style={{
            boxShadow: '0 4px 15px rgba(255,212,0,0.35)'
          }} data-id="element-227">
              <PhoneIcon className="w-4 h-4" data-id="element-228" />
              (862) 407-2386
            </a>
          </div>

          {/* Mobile Toggle */}
          <button type="button" className="md:hidden text-white p-2 rounded-lg hover:bg-white/10 transition-colors" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-229">
            {isMobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-230" /> : <MenuIcon className="w-6 h-6" data-id="element-231" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-232">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} className="md:hidden overflow-hidden" style={{
        borderTop: '1px solid rgba(255,255,255,0.08)'
      }} data-id="element-233">
            <div className="px-4 py-6 flex flex-col gap-1" style={{
          background: 'rgba(10,22,40,0.98)'
        }} data-id="element-234">
              {NAV_LINKS.map((link, i) => <motion.a key={link.name} href={link.href} initial={{
            opacity: 0,
            x: -16
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: i * 0.06
          }} onClick={() => setIsMobileMenuOpen(false)} className="text-gray-200 text-lg font-bold py-3 px-4 rounded-xl hover:bg-white/5 hover:text-white transition-all" data-id="element-235">
                  {link.name}
                </motion.a>)}
              <a href="tel:8624072386" className="mt-4 flex items-center justify-center gap-2 bg-[#FFD400] text-[#0A1628] px-6 py-4 rounded-xl font-bold text-lg uppercase tracking-wide" style={{
            boxShadow: '0 4px 20px rgba(255,212,0,0.3)'
          }} data-id="element-236">
                <PhoneIcon className="w-5 h-5" data-id="element-237" />
                Call (862) 407-2386
              </a>
              <a href="sms:8624072386?body=Hi,%20I%20need%20a%20free%20gutter%20estimate.%20My%20name%20is%20___%20and%20my%20address%20is%20___." className="flex items-center justify-center gap-2 bg-[#7AC943] text-white px-6 py-4 rounded-xl font-bold text-lg uppercase tracking-wide" style={{
            boxShadow: '0 4px 20px rgba(122,201,67,0.3)'
          }} data-id="element-238">
                <MessageSquareIcon className="w-5 h-5" data-id="element-239" />
                Text Us for a Quote
              </a>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}